// shell/useRailCollapsed.ts — tiny Zustand store for the rail's collapsed/expanded UI state.
//
// This is transient chrome state (NOT a personalization axis), so it lives here and persists
// to its own localStorage key — separate from prefsStore. ShellLayout reads `collapsed` to size
// the grid; the Rail's CollapseToggle calls `toggle`.

import { create } from 'zustand';

const KEY = 'truxsocial:railCollapsed';

function readInitial(): boolean {
  try {
    return localStorage.getItem(KEY) === '1';
  } catch {
    // localStorage unavailable (SSR/quota) — default to expanded
    return false;
  }
}

function persist(v: boolean): void {
  try {
    localStorage.setItem(KEY, v ? '1' : '0');
  } catch {
    // localStorage unavailable — ignore
  }
}

interface RailCollapsedState {
  collapsed: boolean;
  toggle: () => void;
  set: (v: boolean) => void;
}

export const useRailCollapsed = create<RailCollapsedState>((set) => ({
  collapsed: readInitial(),
  toggle: () =>
    set((s) => {
      const next = !s.collapsed;
      persist(next);
      return { collapsed: next };
    }),
  set: (v) =>
    set(() => {
      persist(v);
      return { collapsed: v };
    }),
}));

const RIGHT_KEY = 'truxsocial:rightRailCollapsed';
export const useRightRailCollapsed = create<RailCollapsedState>((set) => {
  let collapsed = false;
  try {
    collapsed = localStorage.getItem(RIGHT_KEY) === '1';
  } catch {
    /* unavailable */
  }
  const update = (value: boolean) => {
    try {
      localStorage.setItem(RIGHT_KEY, value ? '1' : '0');
    } catch {
      /* unavailable */
    }
    set({ collapsed: value });
  };
  return {
    collapsed,
    toggle: () =>
      set((s) => {
        const next = !s.collapsed;
        try {
          localStorage.setItem(RIGHT_KEY, next ? '1' : '0');
        } catch {
          /* unavailable */
        }
        return { collapsed: next };
      }),
    set: update,
  };
});
